import type { AccountInfo } from "@azure/msal-browser";

/**
 * Konto-seleksjon per provider (WI 2291).
 *
 * MSAL deler konto-registeret (`msal.account.keys`) på tvers av alle
 * MSAL-instanser på samme origin — kun token-listene er per-clientId. Når både
 * Entra- og External-ID-providere er i bruk, returnerer `getAllAccounts()` derfor
 * begge providernes konti. Å plukke `accounts[0]` blindt gir ikke-deterministisk
 * restore (rekkefølgen i registeret er mutasjonsbestemt): noen ganger riktig
 * konto, noen ganger en annen providers konto («unknown»-bruker), noen ganger
 * ingen → innloggingsskjerm.
 *
 * Denne rene funksjonen velger kontoen som faktisk hører til provideren, basert
 * på providerens authority/knownAuthorities. Diskrimineres på:
 *  - host-match: `account.environment` === authority-host, eller listet i
 *    `knownAuthorities` (dekker B2C/CIAM), ELLER
 *  - tenant-GUID-match: authority-stiens tenant-segment er en GUID og
 *    `account.tenantId` matcher den. Entra har host-aliaser
 *    (`login.microsoftonline.com` / `login.windows.net` / `sts.windows.net`), så
 *    tenant-GUID er en robust fallback der host-match glipper.
 */

export interface ProviderAuthorityConfig {
  authority: string;
  knownAuthorities?: string[];
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function authorityHost (authority: string): string | null {
  try {
    return new URL(authority).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function authorityTenantSegment (authority: string): string | null {
  try {
    const segments = new URL(authority).pathname
      .split("/")
      .filter(s => s.length > 0 && s.toLowerCase() !== "v2.0");
    return segments.length > 0 ? segments[segments.length - 1] : null;
  } catch {
    return null;
  }
}

function accountMatchesProvider (account: AccountInfo, cfg: ProviderAuthorityConfig): boolean {
  const env = account.environment?.toLowerCase();
  if (env) {
    const host = authorityHost(cfg.authority);
    if (host && env === host) {
      return true;
    }
    if (cfg.knownAuthorities?.some(k => k.toLowerCase() === env)) {
      return true;
    }
  }

  const tenantSegment = authorityTenantSegment(cfg.authority);
  if (tenantSegment && GUID_RE.test(tenantSegment) && account.tenantId) {
    return account.tenantId.toLowerCase() === tenantSegment.toLowerCase();
  }

  return false;
}

/**
 * Velg kontoen som hører til provideren `cfg`. Returnerer `null` når ingen
 * konto i `accounts` matcher — kaller da skal IKKE sette en aktiv konto.
 * Blant flere kandidater foretrekkes `activeAccount` hvis den er en kandidat
 * (respekterer MSALs per-clientId aktive-konto-markør), ellers første kandidat.
 */
export function selectProviderAccount (
  accounts: AccountInfo[],
  cfg: ProviderAuthorityConfig,
  activeAccount: AccountInfo | null = null
): AccountInfo | null {
  const candidates = accounts.filter(a => accountMatchesProvider(a, cfg));
  if (candidates.length === 0) {
    return null;
  }

  if (activeAccount && candidates.some(c => c.homeAccountId === activeAccount.homeAccountId)) {
    return activeAccount;
  }

  return candidates[0];
}
